import { Timestamp } from 'firebase/firestore'
import { TaskRegistry } from './TaskRegistry.js'
import { SubKreweApprovalTask } from './tasks/SubKreweApprovalTask.js'
import { SubKreweJoinRequestTask } from './tasks/SubKreweJoinRequestTask.js'

/**
 * TaskFactory creates and deserializes task instances
 */
export const TaskFactory = {
  /**
   * Create a task from Firestore data
   * @param data Task data from Firestore
   * @returns Instantiated task object
   */
  createTaskFromFirestore(data: TaskFirestoreData): Task {
    // Handle special cases for task instantiation based on implementation
    switch (data.taskImplementation) {
      case 'SubKreweJoinRequestTask': {
        // Extract required constructor parameters from metadata
        const { metadata } = data
        const typedMetadata = metadata as Record<string, unknown>
        const memberId = typedMetadata.memberId as string
        const subkreweId = typedMetadata.subkreweId as string
        const memberName = typedMetadata.memberName as string
        const subkreweName = typedMetadata.subkreweName as string

        // Create task with required parameters
        const task = new SubKreweJoinRequestTask(memberId, subkreweId, memberName, subkreweName)

        // Apply common properties from Firestore data
        this.applyFirestoreData(task, data)

        return task
      }

      case 'SubKreweApprovalTask': {
        // Extract required constructor parameters from metadata
        const { metadata } = data
        const typedMetadata = metadata as Record<string, unknown>
        const subkreweId = typedMetadata.subkreweId as string
        const subkreweName = typedMetadata.subkreweName as string
        const captainId = typedMetadata.captainId as string
        const captainName = typedMetadata.captainName as string

        // Create task with required parameters
        const task = new SubKreweApprovalTask(subkreweId, subkreweName, captainId, captainName)

        // Apply common properties from Firestore data
        this.applyFirestoreData(task, data)

        return task
      }

      default: {
        // For tasks that we don't have special handling for,
        // fall back to a generic instantiation approach
        try {
          // Get the class and try to instantiate it
          const TaskClass = TaskRegistry.get(data.taskImplementation as string)
          const task = new TaskClass()

          // Apply common properties
          this.applyFirestoreData(task, data)

          return task
        }
        catch (error) {
          console.error(`Error instantiating task ${data.taskImplementation}:`, error)
          throw new Error(`Failed to instantiate task: ${data.taskImplementation}`)
        }
      }
    }
  },

  /**
   * Apply Firestore data to an existing task instance
   * @param task The task instance to update
   * @param data The Firestore data to apply
   */
  applyFirestoreData(task: Task, data: TaskFirestoreData): void {
    // Convert timestamps to dates
    const convertedData = {
      ...data,
      metadata: data.metadata as Record<string, unknown>,
      createdAt: data.createdAt.toDate(),
      updatedAt: data.updatedAt.toDate(),
      dueDate: data.dueDate?.toDate(),
      completedAt: data.completedAt?.toDate(),
    }

    // Ensure traits is an array
    if (!Array.isArray(task.traits)) {
      task.traits = []
    }

    // Apply properties to the task, being careful about metadata
    const { metadata, ...restData } = convertedData

    // For metadata, merge instead of replace to preserve constructor-set values
    task.metadata = { ...task.metadata, ...metadata }

    // Apply remaining properties
    Object.assign(task, restData)
  },

  /**
   * Create Firestore data from a task
   * @param task Task object
   * @returns Task data ready for Firestore
   */
  createFirestoreData(task: Task): Record<string, unknown> {
    // Extract date fields for conversion
    const { createdAt, updatedAt, dueDate, completedAt, metadata, ...otherData } = task

    // Create a basic serializable object
    const firestoreData: Record<string, unknown> = {
      ...otherData,
      metadata,
      createdAt: this.toFirestoreTimestamp(createdAt),
      updatedAt: this.toFirestoreTimestamp(updatedAt),
    }

    // Add optional date fields if they exist
    if (dueDate) {
      firestoreData.dueDate = this.toFirestoreTimestamp(dueDate)
    }

    if (completedAt) {
      firestoreData.completedAt = this.toFirestoreTimestamp(completedAt)
    }

    // Clean the object for Firestore
    // Remove any functions or properties that would cause issues
    Object.keys(firestoreData).forEach((key) => {
      const value = firestoreData[key]
      if (typeof value === 'function') {
        delete firestoreData[key]
      }
    })

    return firestoreData
  },

  /**
   * Create a new task of a specific type
   * @param type The task type to create
   * @param params Parameters for the task constructor
   * @returns New task instance
   */
  createNewTask(type: string, params: Record<string, unknown>): Task {
    switch (type) {
      case 'subkrewe_join_request':
        return new SubKreweJoinRequestTask(
          params.memberId as string,
          params.subkreweId as string,
          params.memberName as string,
          params.subkreweName as string,
        )
      case 'subkrewe_approval':
        return new SubKreweApprovalTask(
          params.subkreweId as string,
          params.subkreweName as string,
          params.captainId as string,
          params.captainName as string,
        )
      default:
        throw new Error(`Task type not implemented: ${type}`)
    }
  },

  /**
   * Convert a Date to Firestore Timestamp
   * @param date Date to convert
   * @returns Firestore Timestamp
   */
  toFirestoreTimestamp(date: Date): FirebaseFirestore.Timestamp {
    // This is a placeholder, as we would need to import Firestore
    // In a real implementation, we would return:
    // return firebase.firestore.Timestamp.fromDate(date)
    return Timestamp.fromDate(date)
  },
}
